import React, { forwardRef, useState, useMemo, useImperativeHandle } from 'react';
import { Row, Col, Form, Select, Input, InputNumber, DatePicker } from 'antd';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/styles';
import { useMutation } from '@apollo/client';
import { useDispatch } from 'react-redux';

import { getLandlordProperties } from '~actions/reconciliation/bookinglist';
import { bookingLandlordStatus } from '~constants/reconciliation-booking';
import { getSymbolFromCurrency } from '~helpers/currency';
import { dayFormatType, moment } from '~helpers/day';
import {
  MUTATION_UPDATE_RECONCILIATION_STATUS,
  MUTATION_UPDATE_RECONCILIATION_RENT,
} from '../../queries';

import Modal from 'common/components/modal';

const useStyles = makeStyles(theme => ({
  wrapper: {
    '& .ant-modal-header': {
      padding: '32px 0px 24px 60px',
    },
    '& .ant-modal-body': {
      padding: '0px 60px 32px 60px',
    },

    '& .ant-form-item-label': {
      lineHeight: '22px',
      marginBottom: 8,
    },
  },
  extraArea: {
    padding: '12px 8px 0 8px',
    margin: '-16px 0 24px 0 !important',
    backgroundColor: theme.colors.gray2,
  },
  noteLength: {
    position: 'absolute',
    right: 0,
    bottom: -22,
    textAlign: 'right',
    ...theme.typography.body2,
    color: theme.colors.disableColor,
  },
}));

const {
  READY_FOR_APPROVAL,
  PENDING_APPROVAL,
  PENDING_INVOICE,
  INVOICED,
  PARTIALLY_PAID,
  UNCLAIMABLE,
  APPROVED,
  PAID,
} = bookingLandlordStatus;

const UpdateStatusModalComponent = ({ form, refetchData }, ref) => {
  const { getFieldDecorator, getFieldValue, getFieldsValue, validateFields, setFieldsValue } = form;

  const dispatch = useDispatch();
  const classes = useStyles();
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [currRow, setCurrRow] = useState({});
  const [landlordProperties, setLandlordProperties] = useState([]);
  const [currPropertyId, setCurrPropertyId] = useState(null);

  const handleCancelModal = () => {
    setVisible(false);
  };

  const handleSearchProperties = slug => {
    if (slug) {
      dispatch(
        getLandlordProperties({ slug }, result => {
          if (result?.properties?.edges) {
            setLandlordProperties(result?.properties?.edges);
          }
        }),
      );
    }
  };

  const [updateReconciliationStatus, { loading: updateStatusLoading }] = useMutation(
    MUTATION_UPDATE_RECONCILIATION_STATUS,
    {
      fetchPolicy: 'network-only',
      onCompleted: res => {
        if (res?.updateOrderReceivable?.orderReceivable?.id) {
          refetchData?.();
          handleCancelModal();
        }
      },
    },
  );

  const [updateReconciliationRent, { loading: updateRendLoading }] = useMutation(
    MUTATION_UPDATE_RECONCILIATION_RENT,
    {
      fetchPolicy: 'network-only',
      onCompleted: res => {
        if (res?.updateOrderRent?.order?.id) {
          const { status, note } = getFieldsValue();

          updateReconciliationStatus({
            variables: {
              input: {
                id: currRow?.id,
                note,
                status,
              },
            },
          });
        }
      },
    },
  );

  const unitTypeRooms = useMemo(() => {
    const property = landlordProperties?.find(item => item.node.id === currPropertyId);

    return property?.node?.unitTypes?.edges ?? [];
  }, [landlordProperties, currPropertyId]);

  const statusOpts = useMemo(() => {
    const { status } = currRow;

    switch (status) {
      case READY_FOR_APPROVAL:
        return [PENDING_APPROVAL, UNCLAIMABLE, APPROVED];
      case PENDING_APPROVAL:
        return [APPROVED, UNCLAIMABLE, READY_FOR_APPROVAL];
      case PENDING_INVOICE:
        return [INVOICED];
      case INVOICED:
        return [PARTIALLY_PAID, PAID, INVOICED, UNCLAIMABLE, READY_FOR_APPROVAL];
      case PARTIALLY_PAID:
        return [INVOICED, PARTIALLY_PAID, PAID];
      default:
        return [];
    }
  }, [currRow]);

  const handleStatusChange = val => {
    if (val === APPROVED) {
      handleSearchProperties(currRow?.landlord?.slug);
    }
  };

  const handlePropertyChange = val => {
    setCurrPropertyId(val);
    setFieldsValue({
      unitTypeId: undefined,
    });
  };

  const handleConfirmClick = () => {
    validateFields((err, values) => {
      if (!err) {
        const {
          status,
          note,
          invoiceNumber,
          listingPrice,
          totalPrice,
          moveInDate,
          moveOutDate,
          unitTypeId,
        } = values;
        const params = {
          id: currRow?.id,
          note,
          status,
        };

        if (status === INVOICED) {
          params.invoiceNumber = invoiceNumber;
        }

        if (status === APPROVED) {
          updateReconciliationRent({
            variables: {
              input: {
                id: currRow?.order?.id,
                listingPrice: String(listingPrice),
                totalPrice: String(totalPrice),
                moveInDate: moment(moveInDate).format(dayFormatType.FILTERPARAMS),
                moveOutDate: moment(moveOutDate).format(dayFormatType.FILTERPARAMS),
                unitTypeId,
              },
            },
          });
        } else {
          updateReconciliationStatus({
            variables: {
              input: params,
            },
          });
        }
      }
    });
  };

  useImperativeHandle(ref, () => ({
    handleOpenModal: row => {
      setVisible(true);
      setCurrRow(row);
      setCurrPropertyId(row?.order?.propertyId);
    },
  }));

  return (
    <Modal
      width={812}
      visible={visible}
      title={t('cms.reconciliation.lead_generation.update.modal.title')}
      okText={t('cms.reconciliation.lead_generation.update.modal.ok_btn')}
      wrapClassName={classes.wrapper}
      onCancel={handleCancelModal}
      onOk={handleConfirmClick}
      okButtonProps={{
        loading: updateRendLoading || updateStatusLoading,
      }}
      destroyOnClose={true}
    >
      <Form>
        <Row>
          <Col span={24}>
            <Form.Item
              label={t('cms.reconciliation.lead_generation.update.modal.update_status_to')}
            >
              {getFieldDecorator('status', {
                rules: [
                  {
                    required: true,
                    message: t(
                      'cms.reconciliation.lead_generation.update.modal.update_status_to.error',
                    ),
                  },
                ],
              })(
                <Select
                  placeholder={t(
                    'cms.reconciliation.lead_generation.update.modal.update_status_to.placeholder',
                  )}
                  onChange={handleStatusChange}
                >
                  <For of={statusOpts} each="opt">
                    <Select.Option value={opt}>
                      {t(`cms.reconciliation.lead_generation.status.${opt?.toLowerCase()}`)}
                    </Select.Option>
                  </For>
                </Select>,
              )}
            </Form.Item>
          </Col>

          <Choose>
            <When condition={getFieldValue('status') === INVOICED}>
              <Col span={24}>
                <Row className={classes.extraArea} style={{ paddingLeft: 20 }}>
                  <Col style={{ width: 314 }}>
                    <Form.Item
                      label={t('cms.reconciliation.lead_generation.update.modal.invoice_number')}
                      style={{ marginBottom: 20 }}
                    >
                      {getFieldDecorator('invoiceNumber', {
                        rules: [
                          {
                            required: true,
                            message: t(
                              'cms.reconciliation.lead_generation.update.modal.invoice_number.error.empty',
                            ),
                          },
                          {
                            type: 'string',
                            max: 250,
                            message: t(
                              'cms.reconciliation.lead_generation.update.modal.note.error',
                            ),
                          },
                          {
                            validator: (_, value, callback) => {
                              if (value && /\s+/g.test(value)) {
                                return callback(
                                  t(
                                    'cms.reconciliation.lead_generation.update.modal.invoice_number.error.invalid',
                                  ),
                                );
                              }

                              return callback();
                            },
                          },
                        ],
                      })(
                        <Input
                          placeholder={t(
                            'cms.reconciliation.lead_generation.update.modal.invoice_number.placeholder',
                          )}
                        />,
                      )}
                    </Form.Item>
                  </Col>
                </Row>
              </Col>
            </When>
            <When condition={getFieldValue('status') === APPROVED}>
              <Col span={24}>
                <Row className={classes.extraArea} gutter={24}>
                  <Col span={12}>
                    <Form.Item
                      label={t('cms.reconciliation.lead_generation.update.modal.property')}
                    >
                      {getFieldDecorator('propertyId', {
                        initialValue: currRow?.order?.propertyName,
                        rules: [
                          {
                            required: true,
                            message: t(
                              'cms.reconciliation.lead_generation.update.modal.property.placeholder',
                            ),
                          },
                        ],
                      })(
                        <Select
                          placeholder={t(
                            'cms.reconciliation.lead_generation.update.modal.property.placeholder',
                          )}
                          onChange={handlePropertyChange}
                        >
                          <For of={landlordProperties} each="property">
                            <Select.Option value={property.node.id}>
                              {property.node.name}
                            </Select.Option>
                          </For>
                        </Select>,
                      )}
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label={t('cms.reconciliation.lead_generation.update.modal.room')}>
                      {getFieldDecorator('unitTypeId', {
                        initialValue: currRow?.order?.unitTypeId,
                        rules: [
                          {
                            required: true,
                            message: t(
                              'cms.reconciliation.lead_generation.update.modal.room.placeholder',
                            ),
                          },
                        ],
                      })(
                        <Select
                          placeholder={t(
                            'cms.reconciliation.lead_generation.update.modal.room.placeholder',
                          )}
                        >
                          <For of={unitTypeRooms} each="room">
                            <Select.Option value={room.node.id}>{room.node.name}</Select.Option>
                          </For>
                        </Select>,
                      )}
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label={t('cms.reconciliation.lead_generation.update.modal.listing_price')}
                    >
                      {getFieldDecorator('listingPrice', {
                        initialValue: currRow?.order?.listingSalesPrice ?? undefined,
                        rules: [
                          {
                            required: true,
                            message: t(
                              'cms.reconciliation.lead_generation.update.modal.listing_price.error',
                            ),
                          },
                        ],
                      })(
                        <InputNumber
                          formatter={value =>
                            value
                              ? `${getSymbolFromCurrency(
                                  currRow?.order?.currency,
                                )} ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                              : undefined
                          }
                          parser={value => value.replace(/[^0-9.]/g, '')}
                          min={1}
                          precision={2}
                          placeholder={t(
                            'cms.reconciliation.lead_generation.update.modal.listing_price.error',
                          )}
                        />,
                      )}
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label={t('cms.reconciliation.lead_generation.update.modal.total_rent')}
                    >
                      {getFieldDecorator('totalPrice', {
                        initialValue: currRow?.order?.totalRental ?? undefined,
                        rules: [
                          {
                            required: true,
                            message: t(
                              'cms.reconciliation.lead_generation.update.modal.total_rent.error',
                            ),
                          },
                        ],
                      })(
                        <InputNumber
                          formatter={value =>
                            value
                              ? `${getSymbolFromCurrency(
                                  currRow?.order?.currency,
                                )} ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                              : undefined
                          }
                          parser={value => value.replace(/[^0-9.]/g, '')}
                          min={1}
                          precision={2}
                          placeholder={t(
                            'cms.reconciliation.lead_generation.update.modal.total_rent.error',
                          )}
                        />,
                      )}
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label={t('cms.reconciliation.lead_generation.update.modal.move_in')}
                      style={{ marginBottom: 20 }}
                    >
                      {getFieldDecorator('moveInDate', {
                        initialValue: currRow?.order?.moveInDate
                          ? moment(currRow?.order?.moveInDate)
                          : undefined,
                        rules: [
                          {
                            required: true,
                            message: t(
                              'cms.reconciliation.booking.details.edit.modal.datepicker.empty',
                            ),
                          },
                        ],
                      })(
                        <DatePicker
                          placeholder={t(
                            'cms.reconciliation.booking.details.edit.modal.datepicker.placeholder',
                          )}
                          format={dayFormatType.UKFORMAT}
                          picker="month"
                          style={{ width: '100%' }}
                          inputReadOnly
                        />,
                      )}
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label={t('cms.reconciliation.lead_generation.update.modal.move_out')}
                      style={{ marginBottom: 20 }}
                    >
                      {getFieldDecorator('moveOutDate', {
                        initialValue: currRow?.order?.moveOutDate
                          ? moment(currRow?.order?.moveOutDate)
                          : undefined,
                        rules: [
                          {
                            required: true,
                            message: t(
                              'cms.reconciliation.booking.details.edit.modal.datepicker.empty',
                            ),
                          },
                        ],
                      })(
                        <DatePicker
                          placeholder={t(
                            'cms.reconciliation.booking.details.edit.modal.datepicker.placeholder',
                          )}
                          format={dayFormatType.UKFORMAT}
                          picker="month"
                          style={{ width: '100%' }}
                          inputReadOnly
                        />,
                      )}
                    </Form.Item>
                  </Col>
                </Row>
              </Col>
            </When>
          </Choose>

          <Col span={24}>
            <Form.Item
              label={t('cms.reconciliation.lead_generation.update.modal.note')}
              style={{ position: 'relative' }}
            >
              {getFieldDecorator('note', {
                rules: [
                  {
                    type: 'string',
                    max: 250,
                    message: t('cms.reconciliation.lead_generation.update.modal.note.error'),
                  },
                ],
              })(
                <Input.TextArea
                  maxLength={250}
                  placeholder={t(
                    'cms.reconciliation.lead_generation.update.modal.note.placeholder',
                  )}
                  style={{ marginBottom: 0 }}
                />,
              )}
              <span className={classes.noteLength}>{getFieldValue('note')?.length ?? 0}/250</span>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

UpdateStatusModalComponent.displayName = 'UpdateStatusModal';

const UpdateStatusModal = forwardRef(UpdateStatusModalComponent);

export default Form.create({
  name: 'update-status-modal',
})(UpdateStatusModal);
